import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlerts } from '../../context/AlertContext';
import { alertApi } from '../../api/alertApi';
import AlertCard from './AlertCard';
import AcknowledgeReasonModal from './AcknowledgeReasonModal';
import { TableSkeleton } from '../common/LoadingSkeleton';
import '../../styles/alerts.css';

const PER_PAGE = 15;

const AlertsListModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { markAllAsRead, unreadCount, refreshAlerts } = useAlerts();
  const [activeTab, setActiveTab] = useState('all');
  const [showMarkAllModal, setShowMarkAllModal] = useState(false);
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [searchPlate, setSearchPlate] = useState('');

  const [modalAlerts, setModalAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  const pageRef = useRef(1);
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const activeSearchRef = useRef('');

  const fetchAlerts = useCallback(async (page, plate, append) => {
    if (page === 1) setLoading(true);
    else setLoadingMore(true);

    // scope='today': the bell dropdown is today's alerts only - full history lives on
    // the /alerts page (see AlertsListModal.jsx's "View All Alerts" button below).
    const { data, error } = await alertApi.getAlerts(null, page, PER_PAGE, plate || null, null, null, false, 'today');

    if (data && !error) {
      const rows = data.data ?? [];
      const pagination = data.pagination ?? {};
      setModalAlerts(prev => append ? [...prev, ...rows] : rows);
      setTotal(pagination.total ?? 0);
      setHasMore(page < (pagination.lastPage ?? 1));
      pageRef.current = page;
    }

    if (page === 1) setLoading(false);
    else setLoadingMore(false);
  }, []);

  const resetAndFetch = useCallback((plate) => {
    activeSearchRef.current = plate;
    setModalAlerts([]);
    setHasMore(false);
    fetchAlerts(1, plate, false);
  }, [fetchAlerts]);

  // Fetch when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchInput('');
      setSearchPlate('');
      resetAndFetch('');
    }
  }, [isOpen]);

  // Infinite scroll observer
  useEffect(() => {
    if (!isOpen) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchAlerts(pageRef.current + 1, activeSearchRef.current, true);
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [isOpen, hasMore, loadingMore, loading, fetchAlerts]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchPlate(searchInput);
    resetAndFetch(searchInput);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchPlate('');
    resetAndFetch('');
  };

  const handleMarkAllClick = () => setShowMarkAllModal(true);

  const handleMarkAllConfirm = async (reason) => {
    setIsMarkingAll(true);
    await markAllAsRead(reason);
    setIsMarkingAll(false);
    setShowMarkAllModal(false);
    resetAndFetch(activeSearchRef.current);
    refreshAlerts();
  };

  const handleAlertAcknowledged = (alertId) => {
    setModalAlerts(prev => prev.filter(a => a.id !== alertId));
    setTotal(prev => Math.max(0, prev - 1));
  };

  const handleViewAll = () => {
    onClose();
    navigate('/alerts');
  };

  if (!isOpen) return null;

  // Client-side tab filter on loaded data
  const filteredAlerts = modalAlerts.filter(alert => {
    switch (activeTab) {
      case 'critical': return alert.severity === 'critical';
      case 'warning':  return alert.severity === 'warning';
      case 'unread':   return !alert.acknowledged;
      default:         return true;
    }
  });

  const tabs = [
    { id: 'all',      label: 'All',      count: total },
    { id: 'critical', label: 'Critical', count: modalAlerts.filter(a => a.severity === 'critical').length },
    { id: 'warning',  label: 'Warning',  count: modalAlerts.filter(a => a.severity === 'warning').length },
    { id: 'unread',   label: 'Unread',   count: unreadCount },
  ];

  return (
    <>
      <div
        className="alerts-modal-overlay"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="alerts-modal-title"
      >
        <div className="alerts-modal" onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <div className="alerts-modal__header">
            <div className="alerts-modal__header-content">
              <h2 id="alerts-modal-title" className="alerts-modal__title">Alerts</h2>
              <span className="alerts-modal__count">
                {total} alert{total !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="alerts-modal__header-actions">
              <button
                onClick={handleViewAll}
                className="btn-secondary"
                style={{ fontSize: 'var(--font-size-xs)', padding: '0.25rem 0.75rem' }}
              >
                View All Alerts
              </button>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllClick}
                  disabled={isMarkingAll}
                  className="btn-secondary"
                  style={{ fontSize: 'var(--font-size-xs)', padding: '0.25rem 0.75rem' }}
                >
                  {isMarkingAll ? 'Processing...' : 'Mark All Read'}
                </button>
              )}
              <button
                onClick={onClose}
                className="alerts-modal__close"
                aria-label="Close modal"
              >
                <svg className="w-5 h-5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="alerts-modal__tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`alerts-modal__tab ${activeTab === tab.id ? 'alerts-modal__tab--active' : ''}`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className="alerts-modal__tab-count">{tab.count}</span>
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="alerts-modal__search-bar">
            <form onSubmit={handleSearchSubmit} className="alerts-modal__search-form">
              <div className="alerts-search">
                <svg className="alerts-search__icon" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by vehicle..."
                  className="input alerts-search__input"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="alerts-search__clear"
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="btn-primary"
                style={{ fontSize: 'var(--font-size-xs)', padding: '0.375rem 0.75rem', marginLeft: '0.5rem', flexShrink: 0 }}
              >
                Search
              </button>
            </form>
          </div>

          {/* Content */}
          <div className="alerts-modal__content">
            {loading ? (
              <TableSkeleton rows={3} columns={1} />
            ) : (
              <div className="alerts-modal__list">
                {filteredAlerts.length === 0 ? (
                  <div className="alerts-modal__empty">
                    <svg className="alerts-modal__empty-icon" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="alerts-modal__empty-text">
                      {searchPlate
                        ? `No alerts for "${searchPlate}"`
                        : activeTab === 'all'
                          ? 'No alerts — All systems normal'
                          : `No ${activeTab} alerts`
                      }
                    </p>
                  </div>
                ) : (
                  filteredAlerts.map(alert => (
                    <AlertCard
                      key={alert.id}
                      alert={alert}
                      onAcknowledged={handleAlertAcknowledged}
                    />
                  ))
                )}

                {/*
                  Infinite scroll sentinel — always rendered, independent of the
                  active tab's client-side filter. The tab filter is applied on
                  top of the already-loaded pages (the backend for this endpoint
                  doesn't accept a severity/tab param), so a tab with few or no
                  matches on the currently loaded pages must NOT unmount this
                  node: hasMore/total (real server pagination state) keep
                  driving further fetches in the background, and the observer
                  stays attached to a stable node instead of losing its target.
                */}
                <div ref={sentinelRef} className="alerts-modal__sentinel">
                  {loadingMore && (
                    <div className="alerts-modal__load-more">
                      <span className="alerts-modal__spinner" />
                      <span>Loading more...</span>
                    </div>
                  )}
                  {!hasMore && modalAlerts.length > 0 && filteredAlerts.length > 0 && (
                    <p className="alerts-modal__end-text">
                      All {total} alerts loaded
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showMarkAllModal && (
        <AcknowledgeReasonModal
          alert={{
            vehiclePlate: 'All Vehicles',
            doorName: `${unreadCount} unread alert${unreadCount !== 1 ? 's' : ''}`,
          }}
          onConfirm={handleMarkAllConfirm}
          onCancel={() => setShowMarkAllModal(false)}
          isSubmitting={isMarkingAll}
        />
      )}
    </>
  );
};

export default AlertsListModal;
